import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles, Trash2, PenLine, Upload } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const AUTH_KEY = "eviq_admin_token";
const authHeaders = () => {
  const t = typeof window !== "undefined" ? window.localStorage.getItem(AUTH_KEY) : null;
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const EMPTY = { title: "", excerpt: "", body_md: "", tags: "", status: "draft" };

export default function AdminBlogPage() {
  const [authed, setAuthed] = useState(null);
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null); // null | {id?|new, form}
  const [genState, setGenState] = useState("idle");

  useEffect(() => {
    fetch(`${API}/api/admin/me`, { credentials: "include", headers: authHeaders() })
      .then((r) => setAuthed(r.ok))
      .catch(() => setAuthed(false));
  }, []);

  const load = useCallback(() => {
    fetch(`${API}/api/admin/blog?_t=${Date.now()}`, {
      credentials: "include", headers: authHeaders(), cache: "no-store",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(setPosts)
      .catch(() => {});
  }, []);
  useEffect(() => { if (authed) load(); }, [authed, load]);

  const generate = async () => {
    setGenState("busy");
    const r = await fetch(`${API}/api/admin/blog/generate`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json", ...authHeaders() },
    }).catch(() => null);
    setGenState(r && r.ok ? "idle" : "error");
    load();
  };

  const save = async () => {
    const f = editing.form;
    const payload = {
      title: f.title, excerpt: f.excerpt, body_md: f.body_md,
      tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
      status: f.status,
    };
    const isNew = editing.id === "new";
    await fetch(`${API}/api/admin/blog${isNew ? "" : `/${editing.id}`}`, {
      method: isNew ? "POST" : "PUT", credentials: "include",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(payload),
    }).catch(() => {});
    setEditing(null);
    load();
  };

  const setStatus = async (id, status) => {
    await fetch(`${API}/api/admin/blog/${id}`, {
      method: "PUT", credentials: "include",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ status }),
    }).catch(() => {});
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this article permanently?")) return;
    await fetch(`${API}/api/admin/blog/${id}`, {
      method: "DELETE", credentials: "include", headers: authHeaders(),
    }).catch(() => {});
    load();
  };

  const uploadFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      let text = String(reader.result || "");
      let title = file.name.replace(/\.(md|txt|markdown)$/i, "").replace(/[-_]+/g, " ");
      let excerpt = "";
      let tags = "";
      // Seed-style header: TITLE:/EXCERPT:/TAGS: then --- then body
      const sep = text.indexOf("\n---\n");
      if (sep !== -1 && /^TITLE:/im.test(text.slice(0, sep))) {
        const head = text.slice(0, sep);
        const get = (k) => (head.match(new RegExp(`^${k}:(.*)$`, "im")) || [])[1]?.trim() || "";
        title = get("TITLE") || title;
        excerpt = get("EXCERPT");
        tags = get("TAGS");
        text = text.slice(sep + 5).trim();
      } else {
        // Plain markdown: first "# Heading" becomes the title
        const m = text.match(/^#\s+(.+)$/m);
        if (m) {
          title = m[1].trim();
          text = text.replace(m[0], "").trim();
        }
      }
      setEditing({ id: "new", form: { title, excerpt, body_md: text, tags, status: "draft" } });
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  if (authed === false) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <div className="text-center">
          <div className="text-sm text-zinc-300 mb-3">Admin sign-in required.</div>
          <Link to="/admin" className="text-[11px] font-mono uppercase tracking-wider text-[#39FF14]">← Go to admin login</Link>
        </div>
      </div>
    );
  }

  const set = (k) => (e) => setEditing((s) => ({ ...s, form: { ...s.form, [k]: e.target.value } }));

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-blog-page">
      <header className="sticky top-0 z-20 border-b border-white/10 bg-black/85 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/admin" className="text-zinc-400 hover:text-white transition-colors" data-testid="blog-back-admin">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div>
              <div className="text-sm font-semibold">Blog manager</div>
              <div className="font-mono text-[10px] text-zinc-500">
                {posts ? `${posts.rows.length} articles · ${posts.drafts} awaiting approval` : "Loading…"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label
              data-testid="blog-upload-btn"
              className="rounded-full border border-white/20 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-white inline-flex items-center gap-1.5 cursor-pointer"
            >
              <Upload className="w-3 h-3" /> Upload
              <input type="file" accept=".md,.txt,.markdown" className="hidden" onChange={uploadFile} data-testid="blog-upload-input" />
            </label>
            <button
              onClick={() => setEditing({ id: "new", form: { ...EMPTY } })}
              data-testid="blog-new-btn"
              className="rounded-full border border-white/20 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-white inline-flex items-center gap-1.5"
            >
              <PenLine className="w-3 h-3" /> Write
            </button>
            <button
              onClick={generate}
              disabled={genState === "busy"}
              data-testid="blog-generate-btn"
              className="rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider inline-flex items-center gap-1.5 disabled:opacity-50"
              style={{ borderColor: "rgba(57,255,20,0.5)", color: "#39FF14", background: "rgba(57,255,20,0.08)" }}
            >
              <Sparkles className="w-3 h-3" /> {genState === "busy" ? "Writing…" : "AI draft now"}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-3">
        {genState === "error" && (
          <div className="text-[12px] text-[#FF6B57]" data-testid="blog-gen-error">AI draft failed — check backend logs and LLM key balance.</div>
        )}

        {editing && (
          <div className="rounded-2xl border border-[#39FF14]/30 bg-white/[0.02] p-5 space-y-3" data-testid="blog-editor">
            <input
              value={editing.form.title} onChange={set("title")} placeholder="Title"
              data-testid="blog-editor-title"
              className="w-full rounded-lg border border-white/10 bg-black/60 px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#39FF14]/50"
            />
            <input
              value={editing.form.excerpt} onChange={set("excerpt")} placeholder="Excerpt (one sentence shown on the list page)"
              data-testid="blog-editor-excerpt"
              className="w-full rounded-lg border border-white/10 bg-black/60 px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#39FF14]/50"
            />
            <input
              value={editing.form.tags} onChange={set("tags")} placeholder="Tags, comma separated (e.g. charging, winter)"
              data-testid="blog-editor-tags"
              className="w-full rounded-lg border border-white/10 bg-black/60 px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#39FF14]/50"
            />
            <textarea
              value={editing.form.body_md} onChange={set("body_md")} rows={16} placeholder="Article body — markdown (## headings, **bold**, lists)"
              data-testid="blog-editor-body"
              className="w-full rounded-lg border border-white/10 bg-black/60 px-3 py-2.5 text-[13px] font-mono text-white placeholder-zinc-600 focus:outline-none focus:border-[#39FF14]/50 resize-y"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={editing.form.status} onChange={set("status")}
                data-testid="blog-editor-status"
                className="rounded-lg border border-white/10 bg-black/60 px-3 py-2 text-sm text-white"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
              <button onClick={save} data-testid="blog-editor-save" className="brand-gradient text-black font-semibold rounded-md px-5 py-2 text-sm">
                Save
              </button>
              <button onClick={() => setEditing(null)} className="text-sm text-zinc-400 hover:text-white">Cancel</button>
            </div>
          </div>
        )}

        {posts === null ? (
          <div className="text-xs text-zinc-500 font-mono">Loading…</div>
        ) : posts.rows.length === 0 ? (
          <div className="text-xs text-zinc-500" data-testid="blog-admin-empty">
            No articles yet. Hit "AI draft now" or "Write" — the daily AI draft also lands here every morning (11:00 UTC) for your approval.
          </div>
        ) : (
          posts.rows.map((p) => (
            <div
              key={p.id}
              className="rounded-xl border px-4 py-3"
              style={{
                borderColor: p.status === "published" ? "rgba(57,255,20,0.3)" : "rgba(255,176,32,0.35)",
                background: p.status === "published" ? "rgba(57,255,20,0.03)" : "rgba(255,176,32,0.04)",
              }}
              data-testid="blog-admin-row"
            >
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-[13px] text-white font-medium">{p.title}</span>
                <span className="font-mono text-[9px] uppercase tracking-wider" style={{ color: p.status === "published" ? "#39FF14" : "#FFB020" }}>
                  {p.status === "published" ? "live" : "awaiting approval"}
                </span>
                <span className="font-mono text-[10px] text-zinc-500">
                  {p.author} · {new Date(p.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })} · {p.reading_min} min
                </span>
              </div>
              {p.excerpt && <p className="text-[11px] text-zinc-400 leading-relaxed mt-1">{p.excerpt}</p>}
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <button
                  onClick={() => setStatus(p.id, p.status === "published" ? "draft" : "published")}
                  data-testid={`blog-toggle-${p.id}`}
                  className="rounded-full border px-2.5 py-1 font-mono text-[9px] uppercase tracking-wider"
                  style={p.status === "published"
                    ? { borderColor: "rgba(255,176,32,0.5)", color: "#FFB020" }
                    : { borderColor: "rgba(57,255,20,0.5)", color: "#39FF14", background: "rgba(57,255,20,0.08)" }}
                >
                  {p.status === "published" ? "Unpublish" : "Approve & publish"}
                </button>
                <button
                  onClick={() => setEditing({ id: p.id, form: { title: p.title, excerpt: p.excerpt || "", body_md: p.body_md, tags: (p.tags || []).join(", "), status: p.status } })}
                  data-testid={`blog-edit-${p.id}`}
                  className="rounded-full border border-white/15 px-2.5 py-1 font-mono text-[9px] uppercase tracking-wider text-zinc-300"
                >
                  Edit
                </button>
                {p.status === "published" && (
                  <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="font-mono text-[9px] uppercase tracking-wider text-[#00E5FF]">
                    View live ↗
                  </a>
                )}
                <button
                  onClick={() => remove(p.id)}
                  data-testid={`blog-delete-${p.id}`}
                  className="rounded-full border border-white/15 px-2.5 py-1 font-mono text-[9px] uppercase tracking-wider text-zinc-400 hover:text-[#FF6B57] hover:border-[#FF6B57]/40 inline-flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            </div>
          ))
        )}
      </main>
    </div>
  );
}
